import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  MenuItem,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { CustomSelect } from '../../../MUI_custom_component';

const SectionTable = ({ section, note }) => (
  <Box>
    <Typography
      variant='h3'
      sx={{ mb: 0.5 }}
    >
      {section.title}
    </Typography>
    <TableContainer
      component={Paper}
      variant='outlined'
      sx={{
        border: 'none',
        boxShadow: 'none',
        borderRadius: '16px',
        overflowX: 'scroll',
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: '#E5E5E5',
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #8C8C8C',
              },
            }}
          >
            {section.table_header_keys.map((key, i) => (
              <TableCell
                key={key}
                sx={
                  i === section.table_header_keys.length - 1
                    ? { textAlign: 'right' }
                    : {}
                }
              >
                <Typography variant='labelM'>{key}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {section.details?.map((item, idx) => (
            <TableRow
              key={idx}
              sx={{
                backgroundColor: '#fff',
                '& td': {
                  borderTop: '1px solid #D9D9D9',
                  borderBottom: 'none',
                  borderRight: 'none',
                  px: 2,
                  py: 1,
                },
              }}
            >
              <TableCell>
                <Typography variant='p3'>{item.display_name}</Typography>
              </TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <Typography
                    variant='p3'
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {item.value}
                  </Typography>
                  {item.message && (
                    <Typography
                      variant='p4'
                      sx={{ color: '#8C8C8C', whiteSpace: 'nowrap' }}
                    >
                      {item.message}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {section.total && (
            <TableRow
              sx={{
                backgroundColor: '#fff',
                '& td': {
                  borderTop: '1px solid #D9D9D9',
                  borderBottom: 'none',
                  borderRight: 'none',
                  px: 2,
                  py: 1,
                },
              }}
            >
              <TableCell>
                <Typography
                  variant='p3'
                  sx={{ fontWeight: 600 }}
                >
                  {section.total.display_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <Typography
                    variant='p3'
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {section.total.value}
                  </Typography>
                  {section.total.message && (
                    <Typography
                      variant='p4'
                      sx={{ color: '#8C8C8C', whiteSpace: 'nowrap' }}
                    >
                      {section.total.message}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
          {note && (
            <TableRow
              sx={{
                backgroundColor: '#fff',
                '& td': { borderBottom: 'none', px: 2, py: 0.5 },
              }}
            >
              <TableCell colSpan={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberOutlined sx={{ fontSize: 16, color: '#CC0000' }} />
                  <Typography
                    variant='p3'
                    sx={{ color: '#CC0000' }}
                  >
                    {note}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);


const FYDetailsSection = ({ fyDetails = [], note }) => {
  const [selectedFY, setSelectedFY] = useState(fyDetails?.[0]?.title || '');
  const noDataAvailable = !fyDetails || fyDetails.length === 0;

  useEffect(() => {
    if (fyDetails?.[0]?.title) {
      setSelectedFY(fyDetails[0].title);
    }
  }, [fyDetails]);

  const selectedData =
    fyDetails?.find((fy) => fy.title === selectedFY) || fyDetails?.[0] || null;

  return (
    <Box>
      {noDataAvailable ? (
        <Paper
          elevation={0}
          sx={{ p: 2, textAlign: 'center' }}
        >
          <Typography
            variant='body1'
            sx={{ color: '#666' }}
          >
            No data available
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography
            variant='labelS'
            sx={{ color: '#666', mb: 0.5, display: 'block' }}
          >
            Financial Year
          </Typography>
          <CustomSelect
            size='small'
            width='100%'
            value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}
          >
            {fyDetails?.map((fy) => (
              <MenuItem
                key={fy.title}
                value={fy.title}
              >
                {fy.title}
              </MenuItem>
            ))}
          </CustomSelect>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              mt: 4,
            }}
          >
            {selectedData?.sections?.map((section, idx) => (
              <SectionTable
                key={idx}
                section={section}
                note={idx === selectedData.sections.length - 1 ? note : undefined}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default FYDetailsSection;
